/**
 * Billboard sprite for NPCs and other entities.
 *
 * Issue #5: "Sprite steht vertikal in der 3D-Welt", "richtet sich automatisch
 * zur Kamera aus", "Pixelgrafik ohne Glättung anzeigen".
 *
 * Implementation notes:
 *  - The sprite is a thin plane facing -Z by default; we set
 *    `billboardMode = AbstractMesh.BILLBOARDMODE_Y` so the plane yaws to
 *    face the camera but stays vertical (no tilt when the player looks
 *    down/up).
 *  - The texture uses NEAREST sampling so pixels stay sharp at any distance.
 *  - `useAlphaFromDiffuseTexture` + `hasAlpha` make the transparent pixels
 *    of the sprite texture actually let the world show through.
 *  - We also add an optional text label above the sprite's head — handy
 *    for player names (#8) and NPC names.
 */

import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Scene } from '@babylonjs/core/scene';

export interface BillboardOptions {
  name: string;
  texture: Texture;
  position: Vector3;
  /** World-space height of the sprite in meters.  Default 2.0. */
  height?: number;
  /** Alpha threshold for cutout (transparent pixels below this are dropped). */
  alphaCutoff?: number;
}

export interface BillboardHandle {
  mesh: Mesh;
  setPosition(v: Vector3): void;
  getPosition(): Vector3;
  setLabel(text: string | null): void;
  dispose(): void;
}

export function buildBillboard(scene: Scene, opts: BillboardOptions): BillboardHandle {
  const height = opts.height ?? 2.0;

  const plane = MeshBuilder.CreatePlane(opts.name, { width: 1, height: height }, scene);
  plane.position.copyFrom(opts.position);
  plane.billboardMode = AbstractMesh.BILLBOARDMODE_Y;
  plane.isPickable = false;

  const mat = new StandardMaterial(`${opts.name}-mat`, scene);
  mat.diffuseTexture = opts.texture;
  mat.emissiveTexture = opts.texture; // emissive so the sprite stays bright
  mat.specularColor = new Color3(0, 0, 0);
  mat.useAlphaFromDiffuseTexture = true;
  mat.diffuseTexture.hasAlpha = true;
  if (opts.alphaCutoff !== undefined) {
    mat.alphaCutOff = opts.alphaCutoff;
  }
  mat.backFaceCulling = true;
  plane.material = mat;

  // ---- Optional floating label ----
  let labelMesh: Mesh | null = null;
  let labelTex: DynamicTexture | null = null;
  const labelHeight = 0.4;

  function ensureLabel(): { mesh: Mesh; tex: DynamicTexture } {
    if (labelMesh && labelTex) return { mesh: labelMesh, tex: labelTex };
    const tex = new DynamicTexture(
      `${opts.name}-label`,
      { width: 256, height: 64 },
      scene,
      false
    );
    tex.updateSamplingMode(Texture.NEAREST_SAMPLINGMODE);

    const lmat = new StandardMaterial(`${opts.name}-label-mat`, scene);
    lmat.diffuseTexture = tex;
    lmat.emissiveTexture = tex;
    lmat.specularColor = new Color3(0, 0, 0);
    lmat.useAlphaFromDiffuseTexture = true;
    lmat.diffuseTexture.hasAlpha = true;
    lmat.backFaceCulling = false;
    lmat.disableLighting = true;

    const mesh = MeshBuilder.CreatePlane(
      `${opts.name}-labelMesh`,
      { width: 2, height: labelHeight },
      scene
    );
    mesh.parent = plane;
    mesh.position.y = height / 2 + labelHeight;
    mesh.billboardMode = AbstractMesh.BILLBOARDMODE_Y;
    mesh.material = lmat;
    mesh.isPickable = false;

    labelTex = tex;
    labelMesh = mesh;
    return { mesh, tex };
  }

  function setLabel(text: string | null): void {
    if (text === null) {
      if (labelMesh) {
        labelMesh.dispose();
        labelMesh = null;
        labelTex = null;
      }
      return;
    }
    const { tex } = ensureLabel();
    const ctx = tex.getContext() as unknown as CanvasRenderingContext2D;
    ctx.clearRect(0, 0, 256, 64);
    ctx.fillStyle = 'rgba(0,0,0,0.65)';
    ctx.fillRect(0, 0, 256, 64);
    ctx.strokeStyle = '#c0c0c0';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, 254, 62);
    ctx.font = 'bold 28px "Courier New", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(text.slice(0, 24), 128, 36);
    tex.update(false);
  }

  return {
    mesh: plane,
    setPosition(v) {
      plane.position.copyFrom(v);
    },
    getPosition() {
      return plane.position.clone();
    },
    setLabel,
    dispose() {
      if (labelMesh) labelMesh.dispose();
      plane.dispose();
    },
  };
}
